import { useEffect, useMemo, useRef, useState } from "react";

type EmojiEntry = { emoji: string; keywords: string[] };
type EmojiCategory = { name: string; emojis: EmojiEntry[] };

const CATEGORIES: EmojiCategory[] = [
  {
    name: "Husarbeid",
    emojis: [
      { emoji: "🧹", keywords: ["kost", "feie", "rydde", "rydding"] },
      { emoji: "🧽", keywords: ["svamp", "vaske", "oppvask"] },
      { emoji: "🧼", keywords: ["såpe", "vaske"] },
      { emoji: "🧴", keywords: ["flaske", "såpe", "sjampo"] },
      { emoji: "🪣", keywords: ["bøtte", "vaske"] },
      { emoji: "🧺", keywords: ["kurv", "klesvask", "klær", "vaske"] },
      { emoji: "🧻", keywords: ["dopapir", "toalettpapir"] },
      { emoji: "🚿", keywords: ["dusj", "vaske"] },
      { emoji: "🛁", keywords: ["bad", "badekar", "vaske", "badet"] },
      { emoji: "🚽", keywords: ["do", "toalett", "vaske"] },
      { emoji: "🛏️", keywords: ["seng", "sove", "re opp"] },
      { emoji: "🪟", keywords: ["vindu", "vaske"] },
      { emoji: "🚪", keywords: ["dør"] },
      { emoji: "🪑", keywords: ["stol"] },
      { emoji: "🛋️", keywords: ["sofa"] },
      { emoji: "🧦", keywords: ["sokker", "klær"] },
      { emoji: "👕", keywords: ["t-skjorte", "klær"] },
      { emoji: "👖", keywords: ["bukse", "klær"] },
      { emoji: "🧥", keywords: ["jakke", "klær"] },
      { emoji: "🗑️", keywords: ["søppel", "kaste", "søppelkasse"] },
      { emoji: "♻️", keywords: ["resirkulering", "panting", "panting"] },
    ],
  },
  {
    name: "Mat & kjøkken",
    emojis: [
      { emoji: "🍽️", keywords: ["tallerken", "oppvask", "vaske"] },
      { emoji: "🍴", keywords: ["bestikk", "gaffel", "kniv"] },
      { emoji: "🥄", keywords: ["skje"] },
      { emoji: "🔪", keywords: ["kniv", "kutte", "hakke"] },
      { emoji: "🍳", keywords: ["stekepanne", "lage mat", "egg"] },
      { emoji: "🥘", keywords: ["gryte", "mat", "lage mat"] },
      { emoji: "🍲", keywords: ["suppe", "mat"] },
      { emoji: "🫖", keywords: ["tekanne", "te"] },
      { emoji: "☕", keywords: ["kaffe", "kopp"] },
      { emoji: "🥤", keywords: ["drikke", "brus"] },
      { emoji: "🥛", keywords: ["melk"] },
      { emoji: "🍞", keywords: ["brød", "smøre"] },
      { emoji: "🥞", keywords: ["pannekake", "lage mat"] },
      { emoji: "🥗", keywords: ["salat", "lage mat"] },
      { emoji: "🍕", keywords: ["pizza", "lage mat"] },
      { emoji: "🍝", keywords: ["pasta", "spagetti"] },
      { emoji: "🥕", keywords: ["gulrot", "grønnsak"] },
      { emoji: "🍎", keywords: ["eple", "frukt"] },
      { emoji: "🍌", keywords: ["banan", "frukt"] },
      { emoji: "🛒", keywords: ["handle", "handlevogn", "kjøp"] },
    ],
  },
  {
    name: "Hage & ute",
    emojis: [
      { emoji: "🌱", keywords: ["plante", "vanne", "hage", "spire"] },
      { emoji: "🌿", keywords: ["urt", "plante", "hage"] },
      { emoji: "🌳", keywords: ["tre", "hage"] },
      { emoji: "🌷", keywords: ["tulipan", "blomst", "hage"] },
      { emoji: "🌻", keywords: ["solsikke", "blomst"] },
      { emoji: "🪴", keywords: ["potteplante", "plante", "vanne"] },
      { emoji: "💧", keywords: ["dråpe", "vann", "vanne"] },
      { emoji: "🍂", keywords: ["løv", "raking", "høst"] },
      { emoji: "🏡", keywords: ["hus", "hjem"] },
      { emoji: "🚗", keywords: ["bil", "vaske bil"] },
      { emoji: "❄️", keywords: ["snø", "måke"] },
      { emoji: "☀️", keywords: ["sol"] },
    ],
  },
  {
    name: "Skole & lekser",
    emojis: [
      { emoji: "📚", keywords: ["bøker", "lese", "lekser"] },
      { emoji: "📖", keywords: ["bok", "lese"] },
      { emoji: "✏️", keywords: ["blyant", "skrive", "lekser"] },
      { emoji: "✒️", keywords: ["penn"] },
      { emoji: "📝", keywords: ["notat", "skrive", "lekser"] },
      { emoji: "🎒", keywords: ["skolesekk", "skole"] },
      { emoji: "🧮", keywords: ["kuleramme", "matte"] },
      { emoji: "📐", keywords: ["linjal", "matte"] },
      { emoji: "🖍️", keywords: ["fargestift", "tegne"] },
    ],
  },
  {
    name: "Lek & fritid",
    emojis: [
      { emoji: "⚽", keywords: ["fotball", "ball", "sport"] },
      { emoji: "🏀", keywords: ["basket", "ball"] },
      { emoji: "🎮", keywords: ["spill", "gaming"] },
      { emoji: "🎲", keywords: ["terning", "spill"] },
      { emoji: "🧩", keywords: ["puslespill", "lek"] },
      { emoji: "🎨", keywords: ["kunst", "tegne", "male"] },
      { emoji: "🎵", keywords: ["musikk", "sang"] },
      { emoji: "🎹", keywords: ["piano", "musikk"] },
      { emoji: "🎸", keywords: ["gitar", "musikk"] },
      { emoji: "🪀", keywords: ["jojo", "lek"] },
    ],
  },
  {
    name: "Dyr",
    emojis: [
      { emoji: "🐶", keywords: ["hund", "valp", "gå tur"] },
      { emoji: "🐕", keywords: ["hund", "gå tur"] },
      { emoji: "🐱", keywords: ["katt"] },
      { emoji: "🐰", keywords: ["kanin"] },
      { emoji: "🐹", keywords: ["hamster"] },
      { emoji: "🐦", keywords: ["fugl"] },
      { emoji: "🐟", keywords: ["fisk"] },
      { emoji: "🐾", keywords: ["poter", "dyr"] },
    ],
  },
  {
    name: "Trening",
    emojis: [
      { emoji: "🏃", keywords: ["løpe", "trening", "sport"] },
      { emoji: "🚴", keywords: ["sykle", "sykkel"] },
      { emoji: "🏊", keywords: ["svømme"] },
      { emoji: "🧘", keywords: ["yoga"] },
      { emoji: "💪", keywords: ["sterk", "trening"] },
      { emoji: "⛸️", keywords: ["skøyter"] },
      { emoji: "⛷️", keywords: ["ski"] },
    ],
  },
  {
    name: "Personlig",
    emojis: [
      { emoji: "🪥", keywords: ["tannbørste", "pusse tenner"] },
      { emoji: "🦷", keywords: ["tann", "tenner"] },
      { emoji: "💇", keywords: ["klippe hår", "frisør"] },
      { emoji: "😴", keywords: ["sove"] },
      { emoji: "🛌", keywords: ["i seng", "sove"] },
    ],
  },
  {
    name: "Annet",
    emojis: [
      { emoji: "✅", keywords: ["godkjent", "ferdig", "sjekk", "ok"] },
      { emoji: "⭐", keywords: ["stjerne"] },
      { emoji: "🌟", keywords: ["stjerne", "glitre"] },
      { emoji: "🏆", keywords: ["pokal", "vinne"] },
      { emoji: "👍", keywords: ["tommel opp", "bra"] },
      { emoji: "❤️", keywords: ["hjerte", "kjærlighet"] },
      { emoji: "🎁", keywords: ["gave", "present"] },
      { emoji: "💰", keywords: ["penger", "lommepenger"] },
      { emoji: "💵", keywords: ["penger", "sedler"] },
      { emoji: "🎉", keywords: ["fest", "feire"] },
    ],
  },
];

const ALL_EMOJIS = CATEGORIES.flatMap((c) => c.emojis);

interface Props {
  value: string;
  onChange: (emoji: string) => void;
  "data-testid"?: string;
}

export function EmojiPicker({ value, onChange, "data-testid": testId }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", handleKey);
      clearTimeout(t);
    };
  }, [open]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!query) return null;
    return ALL_EMOJIS.filter((e) =>
      e.keywords.some((k) => k.toLowerCase().includes(query)),
    );
  }, [query]);

  function select(emoji: string) {
    onChange(emoji);
    setOpen(false);
    setSearch("");
  }

  return (
    <div ref={wrapperRef} data-testid={testId} style={{ position: "relative" }}>
      <button
        type="button"
        data-testid={testId ? `${testId}-trigger` : undefined}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          height: 44,
          padding: "0 14px",
          border: "1px solid var(--line-2)",
          borderRadius: 10,
          background: "var(--bg)",
          cursor: "pointer",
          fontSize: 14,
          color: "var(--ink-3)",
        }}
      >
        <span style={{ fontSize: 24, lineHeight: 1 }}>{value}</span>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Endre</span>
      </button>
      {open && (
        <>
          <div
            data-testid={testId ? `${testId}-backdrop` : undefined}
            onClick={() => setOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99,
            }}
          />
          <div
            data-testid={testId ? `${testId}-popover` : undefined}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              background: "var(--surface)",
              border: "1px solid var(--line-2)",
              borderRadius: 12,
              boxShadow: "var(--shadow-md)",
              padding: 12,
              width: 360,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: 360,
              display: "flex",
              flexDirection: "column",
              zIndex: 100,
            }}
          >
            <input
              ref={searchRef}
              data-testid={testId ? `${testId}-search` : undefined}
              placeholder="Søk emoji…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                flexShrink: 0,
                height: 38,
                padding: "0 10px",
                border: "1px solid var(--line-2)",
                borderRadius: 8,
                fontSize: 14,
                fontFamily: "inherit",
                color: "var(--ink)",
                background: "var(--bg)",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                overflowY: "auto",
                flex: 1,
                marginTop: 8,
                paddingRight: 2,
              }}
            >
              {filtered ? (
                filtered.length === 0 ? (
                  <div
                    data-testid={testId ? `${testId}-empty` : undefined}
                    style={{
                      padding: 20,
                      textAlign: "center",
                      fontSize: 13,
                      color: "var(--ink-3)",
                    }}
                  >
                    Ingen treff for «{search}»
                  </div>
                ) : (
                  <Grid emojis={filtered} onSelect={select} />
                )
              ) : (
                CATEGORIES.map((cat) => (
                  <div key={cat.name} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "var(--ink-3)",
                        padding: "6px 4px 4px",
                      }}
                    >
                      {cat.name}
                    </div>
                    <Grid emojis={cat.emojis} onSelect={select} />
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Grid({
  emojis,
  onSelect,
}: {
  emojis: EmojiEntry[];
  onSelect: (e: string) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(8, 1fr)",
        gap: 2,
      }}
    >
      {emojis.map((e) => (
        <button
          key={e.emoji}
          type="button"
          data-testid="emoji-option"
          data-emoji={e.emoji}
          title={e.keywords[0]}
          onClick={() => onSelect(e.emoji)}
          onMouseEnter={(ev) => {
            ev.currentTarget.style.background = "var(--bg-2)";
          }}
          onMouseLeave={(ev) => {
            ev.currentTarget.style.background = "transparent";
          }}
          style={{
            fontSize: 22,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            borderRadius: 6,
            height: 34,
            lineHeight: 1,
          }}
        >
          {e.emoji}
        </button>
      ))}
    </div>
  );
}
